import React, { useCallback, useMemo, useState } from 'react';
import { matchesRecommendation } from '../../utils/performanceV2';
import PopBar from './PopBar';
import { cardEntityKeys } from '../../utils/deliveryBlockers';
import CardFeed from './CardFeed';
import MoreFindings from './MoreFindings';
import {
  cardsForRoute, hiddenCardsForRoute, partitionDismissed, partitionApplied, asText, cardInCampaign,
} from '../../utils/performanceCards';
import { diagnosisCards, diagnosisCensus } from '../../utils/diagnosisFindings';
import { tierCards } from '../../utils/findingTier';

/**
 * Diagnostics, Budget & pacing and Recommendations.
 *
 * The three are the same page over a different focused analysis — hero, card
 * feed, methodology disclosure — so they are one component with the copy passed
 * in rather than three files that drift apart. What differs between them is
 * wording and which analysis feeds them, not structure.
 *
 * The hero counts are derived from the cards actually present. None of them is a
 * headline figure the page invents: "10 findings from 3 diagnostic runs" is a
 * count of things on the page, and if the analysis produced nothing the page
 * says the analysis produced nothing.
 */

/** What each tab's "More" holds, in a sentence. */
const MORE_NOTE = {
  diagnostics: 'Entity-level detail, keyword-level findings and minor campaign issues. These '
    + 'entities are serving, or carry too little of their campaign’s spend to lead the list.',
  recommendations: 'Unsupported or unestimated ideas, extensions, Google maintenance, keyword-level '
    + 'items. Context-reviewed findings include the evidence needed to reconsider them.',
  pacing: 'Reads that are too early to judge, not computed, on track, or carry no figure.',
};

export default function CardTab({
  route,
  performanceV2 = false,
  title,
  analysisKind,
  showCampaignFilter = true,
  analyzedCampaignCount,
  unit,              // e.g. "findings from the diagnostic run"
  campaign,
  campaigns: accountCampaigns = [],
  cards,
  missing,
  methodology,       // [[label, node], …] for the "how these are computed" block
  orderedBy = 'priority',
  onApply,
  applying = {},
  onDismiss,
  onReinstate,
  dismissals = [],
  reasonSets = {},
  notes = {},
  suppressed = [],
  googleReview = [],
  // diagnostics only: the run's own findings, per campaign. Folded into the feed
  // as advisory cards — see diagnosisFindings.js.
  diagnoses = [],
  tierContext = null,    // utils/findingTier.tierContext over the runs — usePerformance builds it
  pacingDelta = null,    // pacing drill-down only: grounded movement vs the previous run
}) {
  const [recommendationFilters, setRecommendationFilters] = useState({ campaigns: [], platform: 'all', type: 'all', source: 'all' });
  const inRecommendationScope = useCallback((card) => !performanceV2 || matchesRecommendation(card, recommendationFilters, accountCampaigns), [performanceV2, recommendationFilters, accountCampaigns]);
  const mine = useMemo(() => cardsForRoute(cards, route), [cards, route]);
  const hidden = useMemo(() => hiddenCardsForRoute(cards, route), [cards, route]);
  // Dismissed findings leave the feed but keep their reason in the section below.
  const split = useMemo(
    () => partitionDismissed([...mine, ...hidden], dismissals),
    [mine, hidden, dismissals],
  );
  // Applied findings leave the queue too, into the Applied section: the Apply
  // button's own record, so a card stops offering the change it already made.
  const appliedSplit = useMemo(() => partitionApplied(split.live), [split.live]);

  /**
   * THE DIAGNOSTIC RUN'S OWN FINDINGS, IN THE FEED.
   *
   * They used to render as a block below it, unranked, under a subtitle that
   * said "from the run itself, not the suggestion cards" — the run's real
   * content placed underneath the applyable subset of it. An entity that cannot
   * physically serve outranks most of what was printed above it.
   *
   * Built from the SUGGESTION cards' entity keys, so an entity a card already
   * names does not also arrive as an advisory row: the card is the better half
   * of that pair, because it carries the fix and the button. Then partitioned
   * through the same dismissal rule as everything else, so dismissing one of
   * these behaves like dismissing any other finding rather than looking like it
   * worked and returning on reload.
   */
  const cardKeys = useMemo(
    () => cardEntityKeys(partitionDismissed(cards, dismissals).live.filter((c) => c.route === route || (route === 'diagnostics' && c.alsoReportedBy?.some(source => source.agent === 'Diagnostic')))),
    [cards, dismissals, route],
  );
  // The undismissed run cards, whose account reviews fold the entities they
  // list (diagnosisFindings `covered`).
  const liveRunCards = useMemo(() => partitionDismissed(cards, dismissals).live, [cards, dismissals]);
  const dgnSplit = useMemo(
    () => partitionDismissed(diagnosisCards(diagnoses, cardKeys, liveRunCards), dismissals),
    [diagnoses, cardKeys, liveRunCards, dismissals],
  );
  const feedCards = useMemo(
    () => [...appliedSplit.live, ...dgnSplit.live],
    [appliedSplit.live, dgnSplit.live],
  );
  /**
   * MAIN AND MORE. Every tab's feed leads with what changes what the account
   * does, and the rest waits behind one "More (N)" button at its foot. The
   * rules are findingTier.js's, over the same context the badges use. Every
   * count on this page (hero, PopBar, the dropdown) counts MAIN, so it agrees
   * with the tab badge and the Summary strip; More carries its own count.
   * Hidden turn-on cards are always MAIN, so their toggle behaves as before.
   */
  const tierSplit = useMemo(() => tierCards(feedCards, tierContext), [feedCards, tierContext]);
  const mainCards = tierSplit.main;
  const dismissedCards = useMemo(
    () => [...split.dismissed, ...dgnSplit.dismissed],
    [split.dismissed, dgnSplit.dismissed],
  );

  const visibleMine = useMemo(
    () => mainCards.filter((c) => c.route === route && inRecommendationScope(c)), [mainCards, route, inRecommendationScope],
  );
  const ok = visibleMine.filter((c) => c.auto).length;
  const advisory = visibleMine.length - ok;
  const neverRan = (missing || []).includes(analysisKind);

  // The feed's campaign dropdown scopes the sections below it too — see the note
  // in CardFeed. Held here, reported from there.
  const [campFilter, setCampFilter] = useState('all');
  const onCampaignChange = useCallback((key) => setCampFilter(key), []);

  // Every rolled-up section is account-wide: `byKind` unions each campaign's run
  // of that kind, so the judge's suppressed set describes the whole account
  // while the feed above it describes one campaign. Narrowed here on the stamp
  // usePerformance puts on every row. Test plans and Google reviews are cards in
  // the feed itself, so CardFeed scopes them with every other recommendation.
  // More follows the feed's campaign dropdown and filters, like everything below it.
  const moreShown = useMemo(
    () => tierSplit.more.filter((c) => (campFilter === 'all' || cardInCampaign(c, campFilter)) && inRecommendationScope(c)),
    [tierSplit.more, campFilter, inRecommendationScope],
  );

  const scoped = useCallback(
    (rows) => (campFilter === 'all' ? rows : rows.filter((r) => r._camp === campFilter)),
    [campFilter],
  );
  const shownSuppressed = useMemo(() => scoped(suppressed).filter((r) => inRecommendationScope({
    camp: r._camp, tt: r.target_type || r.entity_type, action: r.action_type || r.type || r.lever, cat: r.category,
    origin: r.origin, dest: r._dest,
  })), [suppressed, scoped, inRecommendationScope]);

  const shownGoogleReviews = useMemo(() => scoped(googleReview).filter(r =>
    !['adopt', 'adapt'].includes((asText(r.verdict) || '').toLowerCase()) && inRecommendationScope({
      camp: r._camp, tt: 'google recommendation', action: r.mapped_lever || r.type,
      cat: 'google-recommendation', origin: 'Google', dest: r._dest,
    })), [googleReview, scoped, inRecommendationScope]);

  // ── What the diagnosis holds that the feed does NOT show ─────────────────
  // Two things, and both have to be said rather than silently subtracted: a
  // reader who counted entities in Google and finds fewer findings here is owed
  // the difference.
  //
  //   OFF      inventory somebody switched off on purpose. Never a card: "you
  //            paused this" is not a finding, and counting every row with a
  //            non-empty `blockers` list is exactly what once read as "520
  //            blocked entities across 31 campaigns" on one account.
  //   COVERED  an entity a suggestion card in this same feed already names.
  //
  // Scoped to the feed's campaign dropdown, so this line and the cards above it
  // always describe the same campaigns.
  const shownDiagnoses = useMemo(
    () => (campFilter === 'all'
      ? diagnoses
      : diagnoses.filter((d) => d.campaignId === campFilter)),
    [diagnoses, campFilter],
  );
  const census = useMemo(
    () => diagnosisCensus(shownDiagnoses, cardKeys, liveRunCards), [shownDiagnoses, cardKeys, liveRunCards],
  );
  // One chip per campaign the feed represents. Built from the runs, so a chip
  // always has cards behind it.
  const campaigns = accountCampaigns.map((c) => ({ key: c.id, label: c.name, destination: c.destination }));

  return (
    <div className="wrap">
      <div className="hero">
        <div className="hero-k">
          <span className="scope-tag">
            {campaign
              ? campaign.name
              : `${analyzedCampaignCount ?? accountCampaigns.length} ${(analyzedCampaignCount ?? accountCampaigns.length) === 1 ? 'campaign' : 'campaigns'} analysed`}
          </span>
          {title}
        </div>
        <div className="hero-n">
          {visibleMine.length} <span className="hero-of">{unit}</span>
        </div>
        <PopBar segments={[
          { cls: 'f-ok', n: ok, label: 'can apply' },
          { cls: 'f-att', n: advisory, label: 'advisory' },
          { cls: 'f-gry', n: feedCards.filter((c) => c.route === 'hidden' && inRecommendationScope(c)).length, label: 'hidden turn-on' },
        ]}
        />
        {/* Both hero sub-lines are additive and tab-exclusive: `diagnoses`
            arrives only on Diagnostics, `pacingDelta` only on a pacing
            drill-down — a merge of PRs #2826 and #2830, kept side by side.

            THE CENSUS. What the run examined and the feed does not list. It
            replaces a sub-line that pointed at "its delivery diagnosis below",
            which no longer exists as a section — those findings are cards now.
            The numbers stay because they are the difference between what Google
            shows and what this page ranks, and a page that silently drops rows
            is indistinguishable from one whose run never produced them. */}
        {census.off || census.covered ? (
          <div className="hero-sub">
            <b>Also examined:</b>
            {census.off ? (
              <>
                {' '}<b>{census.off}</b>
                {census.off === 1 ? ' entity' : ' entities'} switched off on purpose —
                paused, removed or not yet started, which is a decision rather than a fault
              </>
            ) : null}
            {census.off && census.covered ? ' ·' : null}
            {census.covered ? (
              <>
                {' '}<b>{census.covered}</b> blocked
                {census.covered === 1 ? ' entity is' : ' entities are'} not listed separately —
                a card above already names {census.covered === 1 ? 'it' : 'them'}, and carries the fix
              </>
            ) : null}
            .
          </div>
        ) : null}
        {/* Grounded movement since the previous run — the server's diff of the
            code-grounded pacing fields, drill-down only (PerformancePage passes
            it only when one campaign is in view). Numbers only; no verdicts. */}
        {pacingDelta ? (
          <div className="hero-sub hero-delta">
            <b>Since the last run{pacingDelta.previous_run_at
              ? ` (${new Date(pacingDelta.previous_run_at).toLocaleDateString()})`
              : ''}:</b>
            {' '}
            {[
              pacingDelta.projected_variance
                && `projected variance ${Number(pacingDelta.projected_variance.previous).toLocaleString()} → ${Number(pacingDelta.projected_variance.current).toLocaleString()}`,
              pacingDelta.recent_daily_spend
                && `recent daily spend ${Number(pacingDelta.recent_daily_spend.previous).toLocaleString()} → ${Number(pacingDelta.recent_daily_spend.current).toLocaleString()}`,
              pacingDelta.days_at_budget_cap
                && `days at cap ${pacingDelta.days_at_budget_cap.previous} → ${pacingDelta.days_at_budget_cap.current}`,
              pacingDelta.pacing_status
                && pacingDelta.pacing_status.previous !== pacingDelta.pacing_status.current
                && `${pacingDelta.pacing_status.previous} → ${pacingDelta.pacing_status.current}`,
            ].filter(Boolean).join(' · ')}
          </div>
        ) : null}
        {visibleMine.length && !ok ? (
          <div className="hero-sub">
            <b>None of the {visibleMine.length} can be applied from here</b>, so every card is advisory.
            Each one names why in its own body.
          </div>
        ) : null}
      </div>

      <div className="sec-h" style={{ marginTop: 26 }}>
        <div className="sec-t">{route === 'diagnostics' ? 'Findings' : 'Recommendations'}</div>
        <div className="sec-n">Choose priority first or campaign by campaign below</div>
      </div>

      <CardFeed
        showCampaignFilter={showCampaignFilter}
        performanceV2={performanceV2}
        recommendationFilters={recommendationFilters}
        onRecommendationFiltersChange={setRecommendationFilters}
        cards={mainCards}
        campaigns={campaigns}
        orderedBy={orderedBy}
        onApply={onApply}
        applying={applying}
        onDismiss={onDismiss}
        onReinstate={onReinstate}
        dismissedCards={dismissedCards}
        appliedCards={appliedSplit.applied}
        reasonSets={reasonSets}
        notes={notes}
        onCampaignChange={onCampaignChange}
        footer={moreShown.length ? ({ onlyAuto }) => (
          <MoreFindings
            onlyAuto={onlyAuto}
            cards={moreShown}
            note={MORE_NOTE[route]}
            onApply={onApply}
            applying={applying}
            onDismiss={onDismiss}
            reasonSets={reasonSets}
            notes={notes}
          />
        ) : null}
        emptyState={(
          <div className="rc-empty">
            {moreShown.length && !neverRan
              ? (
                <>
                  <b>Nothing here needs to lead.</b> The {analysisKind} run&rsquo;s{' '}
                  {moreShown.length} {moreShown.length === 1 ? 'finding is' : 'findings are'} minor
                  detail, under More below.
                </>
              )
              : neverRan
              ? (
                <>
                  <b>This analysis has not been run for this campaign.</b> Nothing here is empty
                  because the campaign is healthy — the {analysisKind} analysis simply has no
                  completed run to read. Start one from Optimize Campaign.
                </>
              )
              : census.off
                ? (
                  <>
                    <b>The {analysisKind} run found nothing to raise here</b> — but it did look:
                    {' '}{census.off} {census.off === 1 ? 'entity is' : 'entities are'} switched off
                    on purpose, which is a decision rather than a fault, so
                    {census.off === 1 ? ' it is' : ' they are'} counted and not listed.
                  </>
                )
                : (
                  <>
                    <b>The {analysisKind} analysis returned no findings.</b> That is a result, not a
                    gap: the run completed and had nothing to raise.
                  </>
                )}
          </div>
        )}
      />

      {/* THE DELIVERY DIAGNOSIS used to render here: one collapsed block per
          campaign, below the feed, under "N blocked entities across M campaigns
          · from the run itself, not the suggestion cards". It is now IN the
          feed — every row of it is a card, ranked with everything else and
          labelled Advisory, because none of them carries an edit payload. See
          utils/diagnosisFindings.js for what became a card and what did not,
          and the census sub-line above for the rest. */}      {/* ══ THE JUDGE'S SUPPRESSED SET ══
          Shown, not filtered. optimizer_judge MOVES a contradicted suggestion
          into suppressed_suggestions with its reason precisely so a reviewer can
          see what was removed and disagree — a suppression is either a correct
          dedupe or a false positive, and only a reader can tell which. */}
      {shownGoogleReviews.length ? (
        <details className="collapsed-sec">
          <summary><b>{shownGoogleReviews.length} Google reviews outside the action queue</b></summary>
          <p>Deferred, rejected and unreviewed findings remain here for review. They are not accepted recommendations and cannot be applied.</p>
          {shownGoogleReviews.map((review, i) => (
            <div className="mini-l" key={`${review._camp || 'account'}-${i}`}>
              <span className="mini-t">
                <b>{asText(review.type) || 'Google recommendation'}</b>
                <span>Google · {asText(review.verdict) || 'unreviewed'}</span>
                {asText(review.target) ? <span> — {asText(review.target)}</span> : null}
                {asText(review.reasoning) ? <p>{asText(review.reasoning)}</p> : null}
                {asText(review.google_projection) ? <p>{asText(review.google_projection)}</p> : null}
              </span>
              {asText(review._campName) ? <span className="mini-a">{asText(review._campName)}</span> : null}
            </div>
          ))}
        </details>
      ) : null}
      {shownSuppressed.length ? (
        <details className="collapsed-sec">
          <summary>
            <span aria-hidden="true" className="material-icons sec-ico">filter_alt</span>
            <b>{shownSuppressed.length} suppressed by the judge</b> &mdash; kept, because the
            evidence may still stand
            <span aria-hidden="true" className="material-icons rc-chev">expand_more</span>
          </summary>
          {shownSuppressed.map((sg, i) => (
            // eslint-disable-next-line react/no-array-index-key
            <div className="mini-l" key={`${sg.target_id || 'x'}-${i}`}>
              <span className="mini-t">
                <b>{asText(sg.headline || sg.plain_summary || sg.action_type) || 'Suggestion'}</b>
                {asText(sg._suppressed_reason) ? <> &mdash; {asText(sg._suppressed_reason)}</> : null}
              </span>
              {/* Which campaign's judge suppressed it. The set is a union across
                  every campaign's run, so an unattributed row is unreadable. */}
              {campFilter === 'all' && asText(sg._campName) ? (
                <span className="mini-a">
                  <span className="tag tg-gry">{asText(sg._campName)}</span>
                </span>
              ) : null}
            </div>
          ))}
        </details>
      ) : null}

      {methodology?.length ? (
        <details className="d" style={{ marginTop: 24, borderTop: '1px solid var(--line)' }}>
          <summary className="d-s">
            <span aria-hidden="true" className="material-icons">expand_more</span>
            How these numbers are computed
          </summary>
          <div className="d-b">
            {methodology.map(([label, body]) => (
              <React.Fragment key={label}>
                <div className="d-l">{label}</div>
                <div className="d-v mut">{body}</div>
              </React.Fragment>
            ))}
          </div>
        </details>
      ) : null}
    </div>
  );
}
